/**
 * Iframe-side postMessage bridge.
 *
 * The inverse of `src/embed/embed.ts` — this runs inside the chat iframe and
 * talks to the parent window (if one exists). Safe to call from any render
 * environment; no-ops when not embedded.
 */

import { OWNS_DOCUMENT } from '@/lib/build-flags';
import {
	type HostToIframe,
	type IframeToHost,
	isHostToIframe,
	PROTOCOL_VERSION,
} from '@/lib/embed-protocol';

/**
 * Distributive Omit — preserves discriminated union members when stripping
 * a shared field. Plain `Omit<Union, K>` collapses payload-specific fields.
 */
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

export type IframeToHostPayload = DistributiveOmit<IframeToHost, 'v'>;

/**
 * Are we the SPA running inside the loader's (cross-origin) iframe?
 *
 * Always false in the element builds: there the frame around us, if any,
 * belongs to whoever framed the school's page, and the bridge must not talk to
 * it. `OWNS_DOCUMENT` leads so the whole check folds to `false` at build time.
 */
export function isEmbedded(): boolean {
	if (!OWNS_DOCUMENT) return false;
	if (typeof window === 'undefined') return false;
	try {
		return window.self !== window.top;
	} catch {
		// Cross-origin security exception reading window.top → definitely embedded.
		return true;
	}
}

const PARENT_ORIGIN_PARAM = 'parentOrigin';
const PARENT_ORIGIN_STORAGE_KEY = 'reachout:parent-origin';

/**
 * Parent origin explicitly declared by the embed loader via the iframe URL
 * (`?parentOrigin=`). Whoever sets the param IS the embedder, so pinning to
 * it is sound: it only lets the embedding page (which controls the iframe
 * anyway) message us, and shuts out sibling frames / foreign windows.
 * Persisted to sessionStorage so it survives in-app navigations and reloads
 * that strip the search param.
 */
function readDeclaredParentOrigin(): string | null {
	try {
		const raw = new URLSearchParams(window.location.search).get(PARENT_ORIGIN_PARAM);
		if (raw) {
			const parsed = new URL(raw);
			// A bare, well-formed http(s) origin only — rejects paths, credentials,
			// and non-http schemes (their URL.origin never round-trips to the input).
			if ((parsed.protocol === 'https:' || parsed.protocol === 'http:') && parsed.origin === raw) {
				try {
					sessionStorage.setItem(PARENT_ORIGIN_STORAGE_KEY, parsed.origin);
				} catch {
					// Storage unavailable (blocked third-party context) — pin for this
					// document only.
				}
				return parsed.origin;
			}
		}
	} catch {
		// Fall through to the persisted copy.
	}
	try {
		return sessionStorage.getItem(PARENT_ORIGIN_STORAGE_KEY);
	} catch {
		return null;
	}
}

/**
 * Captured eagerly at module evaluation: the router rewrites
 * `location.search` against the route schemas on its very first redirect,
 * which would strip `parentOrigin` before any hook gets to read it.
 */
let cachedDeclaredOrigin: string | null = null;
if (typeof window !== 'undefined' && isEmbedded()) {
	// Guarded on isEmbedded(): mounted in a school's page this would read *their*
	// query string, and `?parentOrigin=` there means nothing to us. getParentOrigin
	// already returns null when not embedded, so this only avoids the stray read.
	cachedDeclaredOrigin = readDeclaredParentOrigin();
}

/** Test hook: re-run the eager capture after mutating URL/sessionStorage. */
export function _resetParentOriginCacheForTests(): void {
	cachedDeclaredOrigin =
		typeof window !== 'undefined' && isEmbedded() ? readDeclaredParentOrigin() : null;
}

/**
 * The origin of the page embedding us: the loader-declared `?parentOrigin=`
 * pin when present, otherwise `document.referrer` (kept as a fallback for
 * hosts still running a cached loader that predates the param).
 */
export function getParentOrigin(): string | null {
	if (!isEmbedded()) return null;
	if (cachedDeclaredOrigin) return cachedDeclaredOrigin;
	try {
		const ref = document.referrer;
		if (!ref) return null;
		return new URL(ref).origin;
	} catch {
		return null;
	}
}

/** Post a typed message to the parent. No-op if not embedded. */
export function postToHost(msg: IframeToHostPayload) {
	if (!isEmbedded()) return;
	const parent = getParentOrigin();
	if (!parent) return;
	const payload = { v: PROTOCOL_VERSION, ...msg } as IframeToHost;
	window.parent.postMessage(payload, parent);
}

let warnedNoParentOrigin = false;

/**
 * Subscribe to messages from the host page. Returns an unsubscribe fn.
 * Enforces protocol version + sender window + origin match. Fails closed:
 * with no pinnable parent origin (no loader param, referrer stripped) no
 * listener is attached at all — host integration goes quiet rather than
 * accepting `reachout:navigate`/`set-user` from arbitrary windows.
 */
export function subscribeToHost(onMessage: (msg: HostToIframe) => void): () => void {
	if (!isEmbedded()) return () => {};
	const expectedOrigin = getParentOrigin();
	if (!expectedOrigin) {
		if (!warnedNoParentOrigin) {
			warnedNoParentOrigin = true;
			console.warn(
				'[reachout] Parent origin unknown (no parentOrigin param and no referrer) — ignoring host messages.',
			);
		}
		return () => {};
	}

	const handler = (event: MessageEvent) => {
		if (event.source !== window.parent) return;
		if (event.origin !== expectedOrigin) return;
		if (!isHostToIframe(event.data)) return;
		onMessage(event.data);
	};

	window.addEventListener('message', handler);
	return () => window.removeEventListener('message', handler);
}
